import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Fade,
  Tooltip,
  IconButton,
  Chip,
} from "@mui/material";

import DoneIcon from "@mui/icons-material/Done";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import DeleteIcon from "@mui/icons-material/Delete";
import ReplyIcon from "@mui/icons-material/Reply";
import Checkbox from "@mui/material/Checkbox";

import AttachmentPreview from "../preview/AttachmentPreview";

/* ----------------------------------------------------------
   CLEAN HTML
---------------------------------------------------------- */
function cleanHtml(html) {
  return html
    .replace(/<!doctype[^>]*>/gi, "")
    .replace(/<meta[^>]*>/gi, "")
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, "")
    .replace(/<!--[^>]*-->/g, "");
}

/* ==========================================================
   BUBBLE WHATSAPP (todo abierto)
========================================================== */
function BubbleWhatsApp({
  email,
  body,
  loadingBody,
  attachments,
  toggleRead,
  openReply,
  openForward,
  askDelete,
  isOutgoing,
}) {
  const bubbleColor = isOutgoing ? "#dff7e8a9" : "#e9effa8f";

  return (
    <Fade in>
      <Box sx={{ mb: 2 }}>
        <Box
          sx={{
            display: "flex",
            maxWidth: "75%",
            ml: isOutgoing ? "auto" : 0,
            borderRadius: isOutgoing
              ? "16px 16px 4px 16px"
              : "16px 16px 16px 4px",
            p: 2,
            boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
          }}
        >
          <Box sx={{ flex: 1, bgcolor: bubbleColor }}>
            <Box sx={{ mb: 1 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
                {email.from}
              </Typography>

              <Typography sx={{ fontSize: 11, color: "#555" }}>
                {new Date(email.date).toLocaleString("es-ES")}
              </Typography>
            </Box>

            {loadingBody ? (
              <CircularProgress size={22} />
            ) : (
              <div
                style={{
                  backgroundColor: "#fff",
                  fontFamily: "Inter, sans-serif",
                  fontSize: 14,
                  lineHeight: 1.55,
                }}
                dangerouslySetInnerHTML={{
                  __html: cleanHtml(body || "").replace(
                    /--matrixboundary--/g,
                    ""
                  ),
                }}
              />
            )}

            {attachments?.length > 0 && (
              <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
                {attachments.map((a) => (
                  <AttachmentPreview key={a.id} file={a} />
                ))}
              </Box>
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "center",
              ml: 1,
            }}
          >
            <Tooltip
              title={
                email.isRead ? "Marcar como no leído" : "Marcar como leído"
              }
            >
              <IconButton
                size="small"
                onClick={() => toggleRead(email.gmailId)}
                sx={{ "&.MuiIconButton-root": { height: 35, width: 35 } }}
              >
                {email.isRead ? (
                  <MailOutlineIcon sx={{ opacity: 0.6, fontSize: "1rem" }} />
                ) : (
                  <DoneIcon sx={{ color: "#69cbf9", fontSize: "1rem" }} />
                )}
              </IconButton>
            </Tooltip>

            <Tooltip title="Responder">
              <IconButton
                size="small"
                onClick={() => openReply(email)}
                sx={{ "&.MuiIconButton-root": { height: 35, width: 35 } }}
              >
                <ReplyIcon sx={{ opacity: 0.6, fontSize: "1rem" }} />
              </IconButton>
            </Tooltip>

            <Tooltip title="Reenviar">
              <IconButton
                size="small"
                onClick={() => openForward(email)}
                sx={{ "&.MuiIconButton-root": { height: 35, width: 35 } }}
              >
                <ReplyIcon
                  sx={{
                    opacity: 0.6,
                    fontSize: "1rem",
                    transform: "scaleX(-1)",
                  }}
                />
              </IconButton>
            </Tooltip>

            <Tooltip title="Eliminar">
              <IconButton
                size="small"
                onClick={() => askDelete(email.gmailId)}
                sx={{ "&.MuiIconButton-root": { height: 35, width: 35 } }}
              >
                <DeleteIcon sx={{ color: "#d9534f", fontSize: "1rem" }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </Box>
    </Fade>
  );
}

/* ==========================================================
   BUBBLE EMAIL (cerrado + se abre al clic)
========================================================== */
function BubbleEmail({
  email,
  selectedEmail,
  onSelect,
  body,
  loadingBody,
  attachments,
  toggleRead,
  openReply,
  openForward,
  askDelete,
  getAccountColor,
  selectionMode,
  selected,
  toggleSelectEmail,
}) {
  const bubbleColor = email.direction === "sent" ? "#dff7e8" : "#e9effa";
  const isOpen = selectedEmail?.gmailId === email.gmailId;

  return (
    <Fade in>
      <Box sx={{ mb: 2 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            gap: 1,
            "&:hover .email-checkbox": { opacity: 1 },
          }}
        >
          {/* CHECKBOX GMAIL */}
          <Checkbox
            className="email-checkbox"
            checked={selected}
            onClick={(e) => {
              e.stopPropagation();
              toggleSelectEmail(email.gmailId);
            }}
            sx={{
              opacity: selected ? 1 : 0,
              transition: "opacity 0.15s",
              p: 0,
              mt: 1,
              ml: -1,
            }}
          />

          {/* BURBUJA */}
          <Box sx={{ flex: 1 }}>
            <Box
              onClick={() => !selectionMode && onSelect(email)}
              sx={{
                display: "flex",
                maxWidth: "75%",
                ml: email.direction === "sent" ? "auto" : 0,
                bgcolor: bubbleColor,
                borderRadius: "16px",
                p: 2,
                cursor: selectionMode ? "default" : "pointer",
                boxShadow: "0 4px 14px rgba(0,0,0,0.08)",
                transition: "0.15s",
                "&:hover": { boxShadow: "0 6px 18px rgba(0,0,0,0.15)" },
                position: "relative",
              }}
            >
              <Box sx={{ flex: 1 }}>
                {/* CHIP D+ (Enviado desde / Recibido de) */}
                {/* <Chip
                  icon={
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        bgcolor: getAccountColor(email),
                      }}
                    />
                  }
                  label={
                    email.direction === "sent"
                      ? `Enviado desde · ${email.to_email}`
                      : `Recibido de · ${email.from}`
                  }
                  size="small"
                  sx={{
                    mb: 1,
                    fontSize: 11,
                    bgcolor: email.direction === "sent" ? "#d1fae5" : "#dbeafe",
                    color: email.direction === "sent" ? "#065f46" : "#1e3a8a",
                    "& .MuiChip-icon": {
                      ml: 0.5,
                      mr: -0.5,
                    },
                  }}
                />
 */}
                {/* Fecha */}
                <Typography sx={{ fontSize: 12, color: "#555" }}>
                  {new Date(email.date).toLocaleString("es-ES")}
                </Typography>

                {/* CABECERA ESTILO OUTLOOK */}
                <Box
                  sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}
                >
                  <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
                    Asunto:
                  </Typography>
                  <Typography sx={{ fontSize: 15, fontWeight: 700 }}>
                    {email.subject}
                  </Typography>
                </Box>

                <Box
                  sx={{ mb: 1, display: "flex", alignItems: "center", gap: 1 }}
                >
                  <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
                    De:
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    <Chip
                      label={email.from}
                      size="small"
                      variant="outlined"
                      color="primary"
                    />
                  </Box>
                </Box>

                <Box
                  sx={{
                    mb: 1,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Typography sx={{ fontSize: 12, fontWeight: 600, mr: 1 }}>
                    Para:
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {email.to_email?.split(",").map((t) => (
                      <Chip
                        key={t.trim()}
                        label={t.trim()}
                        size="small"
                        variant="outlined"
                        color="secondary"
                        sx={{ background: "#e7f3ff" }}
                      />
                    ))}
                  </Box>
                </Box>

                {email.cc_email && (
                  <Box
                    sx={{
                      mb: 1,
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
                      CC:
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {email.cc_email?.split(",").map((c) => (
                        <Chip
                          key={c.trim()}
                          label={c.trim()}
                          size="small"
                          sx={{ background: "#faf0c4" }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}

                {email.bcc_email && (
                  <Box sx={{ mb: 1 }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
                      BCC:
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {email.bcc_email?.split(",").map((b) => (
                        <Chip
                          key={b.trim()}
                          label={b.trim()}
                          size="small"
                          sx={{ background: "#e5e5e5" }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Chips automáticos */}
                <Box sx={{ mt: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
                  {email.snippet?.includes("factura") && (
                    <Chip
                      label="Facturación"
                      size="small"
                      sx={{ backgroundColor: "#69f9ce8e", color: "#444" }}
                    />
                  )}

                  {email.hasAttachments && (
                    <Chip
                      label="Adjuntos"
                      size="small"
                      sx={{ backgroundColor: "#69cbf9", color: "#444" }}
                    />
                  )}

                  {email.from?.includes("@gmail.com") && (
                    <Chip label="Personal" size="small" variant="outlined" />
                  )}
                </Box>

                {/* Snippet */}
                {!isOpen && (
                  <Typography
                    sx={{
                      fontSize: 11,
                      color: "#444",
                      mt: 1,
                      overflow: "hidden",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {email.snippet}
                  </Typography>
                )}
              </Box>

              {/* BOTONES */}
              <Box sx={{ display: "flex", width: "fit-content", ml: 1 }}>
                <Tooltip
                  title={
                    email.isRead ? "Marcar como no leído" : "Marcar como leído"
                  }
                >
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleRead(email.gmailId);
                    }}
                    sx={{ "&.MuiIconButton-root": { height: 35, width: 35 } }}
                  >
                    {email.isRead ? (
                      <MailOutlineIcon
                        sx={{ opacity: 0.6, fontSize: "1rem" }}
                      />
                    ) : (
                      <DoneIcon sx={{ color: "#69cbf9", fontSize: "1rem" }} />
                    )}
                  </IconButton>
                </Tooltip>

                <Tooltip title="Responder">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      openReply(email);
                    }}
                    sx={{ "&.MuiIconButton-root": { height: 35, width: 35 } }}
                  >
                    <ReplyIcon sx={{ opacity: 0.6, fontSize: "1rem" }} />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Reenviar">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      openForward(email);
                    }}
                    sx={{ "&.MuiIconButton-root": { height: 35, width: 35 } }}
                  >
                    <ReplyIcon
                      sx={{
                        opacity: 0.6,
                        fontSize: "1rem",
                        transform: "scaleX(-1)",
                      }}
                    />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Eliminar">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      askDelete(email.gmailId);
                    }}
                    sx={{ "&.MuiIconButton-root": { height: 35, width: 35 } }}
                  >
                    <DeleteIcon sx={{ color: "#d9534f", fontSize: "1rem" }} />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {/* PANEL EXPANDIDO */}
            {isOpen && (
              <Box
                sx={{
                  mt: 1,
                  maxWidth: "75%",
                  ml: email.direction === "sent" ? "auto" : 0,
                  bgcolor: "white",
                  borderRadius: 2,
                  p: 2,
                  border: "1px solid #ddd",
                  boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
                }}
              >
                {loadingBody ? (
                  <CircularProgress size={22} />
                ) : (
                  <div
                    style={{
                      fontFamily: "Inter, sans-serif",
                      fontSize: 14,
                      lineHeight: 1.55,
                    }}
                    dangerouslySetInnerHTML={{
                      __html: cleanHtml(body || "").replace(
                        /--matrixboundary--/g,
                        ""
                      ),
                    }}
                  />
                )}

                {/* Adjuntos */}
                {attachments?.length > 0 && (
                  <Box
                    sx={{
                      mt: 2,
                      display: "flex",
                      gap: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    {attachments.map((a) => (
                      <AttachmentPreview key={a.id} file={a} />
                    ))}
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </Fade>
  );
}

/* ==========================================================
   PANEL DERECHO COMPLETO
========================================================== */
export default function EmailPanelDerecho({
  threads,
  selectedPerson,
  modoVista,
  bodyCache,
  attachmentsByEmail,
  toggleRead,
  openReply,
  openForward,
  askDelete,
  loadBodyCached,
  loadAttachments,
  getAccountColor,
  selectionMode,
  selectionBarMode,
  selectedEmails,
  toggleSelectEmail,
  clearSelection,
}) {
  const [selectedEmail, setSelectedEmail] = useState(null);

  /* Cargar cuerpos + adjuntos */
  useEffect(() => {
    if (!selectedPerson) return;
    const mails = threads[selectedPerson] || [];
    mails.forEach((m) => {
      loadBodyCached(m.gmailId);
      loadAttachments(m.gmailId);
    });
  }, [selectedPerson, threads, loadBodyCached, loadAttachments]);

  if (!selectedPerson) {
    return (
      <Box sx={{ mt: 5, ml: 2, textAlign: "center", color: "#888" }}>
        Selecciona una conversación
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flex: 1,
        overflowY: "auto",
        p: 3,
        bgcolor: "#fafafa",
        pt: selectionMode && selectionBarMode === "top" ? 7 : 3,
      }}
    >
      {(threads[selectedPerson] || []).map((email) => {
        const body = bodyCache[email.gmailId] || "";
        const loading = !bodyCache[email.gmailId];
        const attachments = attachmentsByEmail[email.gmailId] || [];

        return modoVista === "whatsapp" ? (
          <BubbleWhatsApp
            key={email.gmailId}
            email={email}
            body={body}
            loadingBody={loading}
            attachments={attachments}
            toggleRead={toggleRead}
            openReply={openReply}
            openForward={openForward}
            askDelete={askDelete}
            isOutgoing={email.direction === "sent"}
          />
        ) : (
          <BubbleEmail
            key={email.gmailId}
            email={email}
            selectedEmail={selectedEmail}
            onSelect={(email) => {
              if (selectionMode) return;

              setSelectedEmail((prev) =>
                prev?.gmailId === email.gmailId ? null : email
              );

              // 🔥 SOLUCIÓN: Cargar body y adjuntos si aún no están
              loadBodyCached(email.gmailId);
              loadAttachments(email.gmailId);
            }}
            body={body}
            loadingBody={loading}
            attachments={attachments}
            toggleRead={toggleRead}
            openReply={openReply}
            openForward={openForward}
            askDelete={askDelete}
            getAccountColor={getAccountColor}
            selectionMode={selectionMode}
            selected={selectedEmails.includes(email.gmailId)}
            toggleSelectEmail={toggleSelectEmail}
          />
        );
      })}
    </Box>
  );
}
